import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Upload as UploadIcon, Music, Image, Youtube, BookOpen, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const genres = [
  'Worship',
  'Gospel',
  'Contemporary Christian',
  'Praise',
  'Hymns',
  'Christian Rock',
  'Christian Hip Hop',
  'Devotional',
  'Instrumental'
];

const musicalKeys = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const Upload = () => {
  const { token, isArtist } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [songId, setSongId] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    genre: '',
    bpm: '',
    key: '',
    scripture_ref: '',
    lyrics: '',
    description: '',
    is_free: true,
    price: '',
    youtube_url: ''
  });
  
  const [audioFile, setAudioFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ audio: 0, cover: 0 });

  if (!isArtist) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <Music className="h-16 w-16 mx-auto mb-4 text-neutral-700" />
            <h2 className="font-heading text-2xl font-bold text-white mb-2">Artist Account Required</h2>
            <p className="text-neutral-400 mb-6">You need an artist account to upload music.</p>
            <Button 
              onClick={() => navigate('/register')}
              className="bg-m4-yellow text-black hover:bg-m4-yellow/90 font-bold"
            >
              Join as Artist
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.genre) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/songs`, {
        ...formData,
        bpm: formData.bpm ? parseInt(formData.bpm) : null,
        price: formData.price ? parseFloat(formData.price) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSongId(response.data.id);
      setStep(2);
      toast.success('Song details saved!');
    } catch (error) {
      console.error('Failed to create song:', error);
      toast.error(error.response?.data?.detail || 'Failed to save song details');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!songId) return;
    
    setLoading(true);
    try {
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      };

      // Upload audio
      if (audioFile) {
        const audioFormData = new FormData();
        audioFormData.append('file', audioFile);
        
        await axios.post(`${API}/songs/${songId}/upload-audio`, audioFormData, {
          headers,
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, audio: percent }));
          }
        });
      }

      // Upload cover
      if (coverFile) {
        const coverFormData = new FormData();
        coverFormData.append('file', coverFile);
        
        await axios.post(`${API}/songs/${songId}/upload-cover`, coverFormData, {
          headers,
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, cover: percent }));
          }
        });
      }

      setStep(3);
      toast.success('Upload complete!');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload files. You can try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="py-8 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl">
          {/* Header */}
          <div className="mb-8">
            <p className="text-sm text-m4-yellow uppercase tracking-widest mb-2">Create</p>
            <h1 className="font-heading text-4xl font-bold text-white">
              UPLOAD MUSIC
            </h1>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-4 mb-8">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 ${step >= s ? 'text-m4-yellow' : 'text-neutral-600'}`}>
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center border-2 ${
                    step >= s ? 'border-m4-yellow bg-m4-yellow/10' : 'border-neutral-700'
                  }`}>
                    {step > s ? <CheckCircle className="h-5 w-5" /> : s}
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">
                    {s === 1 ? 'Details' : s === 2 ? 'Files' : 'Complete'}
                  </span>
                </div>
                {s < 3 && <div className={`flex-1 h-0.5 ${step > s ? 'bg-m4-yellow' : 'bg-neutral-800'}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Song Details */}
          {step === 1 && (
            <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8">
              <form onSubmit={handleStep1Submit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="title" className="text-neutral-300">Song Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter song title"
                      className="bg-neutral-800 border-neutral-700 rounded-xl text-white placeholder:text-neutral-500 focus:border-m4-yellow"
                      data-testid="song-title"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="genre" className="text-neutral-300">Genre *</Label>
                    <Select value={formData.genre} onValueChange={(value) => setFormData({ ...formData, genre: value })}>
                      <SelectTrigger className="bg-neutral-800 border-neutral-700 rounded-xl text-white" data-testid="song-genre">
                        <SelectValue placeholder="Select genre" />
                      </SelectTrigger>
                      <SelectContent className="bg-neutral-900 border-neutral-800">
                        {genres.map((genre) => (
                          <SelectItem key={genre} value={genre} className="text-white hover:bg-neutral-800">
                            {genre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="bpm" className="text-neutral-300">BPM</Label>
                    <Input
                      id="bpm"
                      name="bpm"
                      type="number"
                      value={formData.bpm}
                      onChange={handleChange}
                      placeholder="120"
                      className="bg-neutral-800 border-neutral-700 rounded-xl text-white placeholder:text-neutral-500 focus:border-m4-yellow"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="key" className="text-neutral-300">Key</Label>
                    <Select value={formData.key} onValueChange={(value) => setFormData({ ...formData, key: value })}>
                      <SelectTrigger className="bg-neutral-800 border-neutral-700 rounded-xl text-white">
                        <SelectValue placeholder="Select key" />
                      </SelectTrigger>
                      <SelectContent className="bg-neutral-900 border-neutral-800">
                        {musicalKeys.map((key) => (
                          <SelectItem key={key} value={key} className="text-white hover:bg-neutral-800">
                            {key}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="scripture_ref" className="text-neutral-300">Scripture</Label>
                    <Input
                      id="scripture_ref"
                      name="scripture_ref"
                      value={formData.scripture_ref}
                      onChange={handleChange}
                      placeholder="John 3:16"
                      className="bg-neutral-800 border-neutral-700 rounded-xl text-white placeholder:text-neutral-500 focus:border-m4-yellow"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-neutral-300">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Tell us about this song..."
                    rows={3}
                    className="bg-neutral-800 border-neutral-700 rounded-xl text-white placeholder:text-neutral-500 focus:border-m4-yellow"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="lyrics" className="text-neutral-300">
                      Lyrics
                      <span className="text-xs text-neutral-500 ml-2">Use [Ref: Verse] for scripture tags</span>
                    </Label>
                    <BookOpen className="h-4 w-4 text-neutral-500" />
                  </div>
                  <Textarea
                    id="lyrics"
                    name="lyrics"
                    value={formData.lyrics}
                    onChange={handleChange}
                    placeholder="Enter lyrics here...&#10;Use [Ref: John 3:16] to tag scripture references"
                    rows={8}
                    className="bg-neutral-800 border-neutral-700 rounded-xl text-white placeholder:text-neutral-500 focus:border-m4-yellow font-scripture"
                    data-testid="song-lyrics"
                  />
                </div>

                <div className="border-t border-neutral-800 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Label className="text-neutral-300">Pricing</Label>
                      <p className="text-xs text-neutral-500">Free streaming, paid downloads</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={formData.is_free ? 'text-m4-yellow' : 'text-neutral-500'}>Free</span>
                      <Switch
                        checked={!formData.is_free}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_free: !checked })}
                      />
                      <span className={!formData.is_free ? 'text-m4-yellow' : 'text-neutral-500'}>Paid</span>
                    </div>
                  </div>
                  
                  {!formData.is_free && (
                    <div className="space-y-2">
                      <Label htmlFor="price" className="text-neutral-300">Download Price (₹)</Label>
                      <Input
                        id="price"
                        name="price"
                        type="number"
                        value={formData.price}
                        onChange={handleChange}
                        placeholder="99"
                        min="1"
                        className="bg-neutral-800 border-neutral-700 rounded-xl text-white placeholder:text-neutral-500 focus:border-m4-yellow"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="youtube_url" className="text-neutral-300 flex items-center gap-2">
                    <Youtube className="h-4 w-4 text-red-500" />
                    YouTube Link
                  </Label>
                  <Input
                    id="youtube_url"
                    name="youtube_url"
                    value={formData.youtube_url}
                    onChange={handleChange}
                    placeholder="https://youtube.com/watch?v=..."
                    className="bg-neutral-800 border-neutral-700 rounded-xl text-white placeholder:text-neutral-500 focus:border-m4-yellow"
                    data-testid="youtube-url-input"
                  />
                  <p className="text-xs text-neutral-500">
                    Add a YouTube link to embed the video on your profile. You can skip uploading an audio file if you provide a YouTube link.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-m4-yellow text-black hover:bg-m4-yellow/90 font-bold uppercase tracking-wider py-6"
                  data-testid="save-details-btn"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Save & Continue'}
                </Button>
              </form>
            </div>
          )}

          {/* Step 2: File Uploads */}
          {step === 2 && (
            <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8 space-y-8">
              {/* Audio Upload */}
              <div className="space-y-4">
                <Label className="text-neutral-300 text-lg flex items-center gap-2">
                  <Music className="h-5 w-5 text-m4-yellow" />
                  Audio File
                </Label>
                <div className="border-2 border-dashed border-neutral-700 p-8 text-center hover:border-m4-yellow/50 transition-colors">
                  <input
                    type="file"
                    accept=".mp3,.wav,.flac,.m4a"
                    onChange={(e) => setAudioFile(e.target.files[0])}
                    className="hidden"
                    id="audio-upload"
                  />
                  <label htmlFor="audio-upload" className="cursor-pointer">
                    <UploadIcon className="h-12 w-12 mx-auto mb-4 text-neutral-500" />
                    {audioFile ? (
                      <p className="text-m4-yellow font-medium">{audioFile.name}</p>
                    ) : (
                      <>
                        <p className="text-white font-medium">Click to upload audio</p>
                        <p className="text-sm text-neutral-500 mt-1">MP3, WAV, FLAC, M4A (max 50MB)</p>
                      </>
                    )}
                  </label>
                </div>
                {uploadProgress.audio > 0 && uploadProgress.audio < 100 && (
                  <div className="h-2 bg-neutral-800 rounded-full overflow-hidden">
                    <div className="h-full bg-m4-yellow transition-all" style={{ width: `${uploadProgress.audio}%` }} />
                  </div>
                )}
              </div>

              {/* Cover Upload */}
              <div className="space-y-4">
                <Label className="text-neutral-300 text-lg flex items-center gap-2">
                  <Image className="h-5 w-5 text-m4-yellow" />
                  Cover Art
                </Label>
                <div className="border-2 border-dashed border-neutral-700 p-8 text-center hover:border-m4-yellow/50 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setCoverFile(e.target.files[0])}
                    className="hidden"
                    id="cover-upload"
                  />
                  <label htmlFor="cover-upload" className="cursor-pointer">
                    <Image className="h-12 w-12 mx-auto mb-4 text-neutral-500" />
                    {coverFile ? (
                      <p className="text-m4-yellow font-medium">{coverFile.name}</p>
                    ) : (
                      <>
                        <p className="text-white font-medium">Click to upload cover art</p>
                        <p className="text-sm text-neutral-500 mt-1">PNG, JPG (recommended 1400x1400px)</p>
                      </>
                    )}
                  </label>
                </div>
                {uploadProgress.cover > 0 && uploadProgress.cover < 100 && (
                  <div className="h-2 bg-neutral-800 rounded-full overflow-hidden">
                    <div className="h-full bg-m4-yellow transition-all" style={{ width: `${uploadProgress.cover}%` }} />
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={() => setStep(3)}
                  variant="outline"
                  className="flex-1 border-neutral-700 text-white hover:bg-neutral-800"
                >
                  Skip for Now
                </Button>
                <Button
                  onClick={handleFileUpload}
                  disabled={loading || (!audioFile && !coverFile)}
                  className="flex-1 bg-m4-yellow text-black hover:bg-m4-yellow/90 font-bold"
                  data-testid="upload-files-btn"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Upload Files'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Complete */}
          {step === 3 && (
            <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8 text-center">
              <div className="h-20 w-20 rounded-full bg-m4-yellow/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-m4-yellow" />
              </div>
              <h2 className="font-heading text-3xl font-bold text-white mb-2">
                UPLOAD COMPLETE!
              </h2>
              <p className="text-neutral-400 mb-8">
                Your song has been uploaded and is now live on M4manna.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate(`/song/${songId}`)}
                  className="bg-m4-yellow text-black hover:bg-m4-yellow/90 font-bold"
                >
                  View Song
                </Button>
                <Button
                  onClick={() => {
                    setStep(1);
                    setSongId(null);
                    setFormData({
                      title: '', genre: '', bpm: '', key: '', scripture_ref: '',
                      lyrics: '', description: '', is_free: true, price: '', youtube_url: ''
                    });
                    setAudioFile(null);
                    setCoverFile(null);
                  }}
                  variant="outline"
                  className="border-neutral-700 text-white hover:bg-neutral-800"
                >
                  Upload Another
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Upload;
